/* STAVRODIA (01.11.2026): Bilder-Slider in Produktkarten (wie Holzkern)
   + Startseite (Desktop): beim Drüberfahren mit der Maus sanft das 2. Bild einblenden
   02.11.2026: Karten mit Farbkreisen (z. B. "Ergänze den Look") zeigen nur die Bilder
   der gewählten Farbe; Klick auf einen Farbkreis tauscht die Bilder aus. */
if (!customElements.get('stav-card-slider')) {
  const STAV_IS_HOME = /^\/([a-z]{2}(-[a-z]{2})?\/?)?$/i.test(location.pathname);
  const STAV_CAN_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');

  class StavCardSlider extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.track = this.querySelector('.stav-slider__track');
      this.bar = this.querySelector('.stav-slider__bar');
      if (!this.track) return;

      this.track.addEventListener('scroll', () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.update();
        });
      }, { passive: true });

      const stop = (event) => {
        event.preventDefault();
        event.stopPropagation();
      };

      this.querySelector('.stav-slider__arrow--prev')?.addEventListener('click', (event) => {
        stop(event);
        const from = this.resolveHover();
        this.go(from - 1);
      });

      this.querySelector('.stav-slider__arrow--next')?.addEventListener('click', (event) => {
        stop(event);
        const from = this.resolveHover();
        this.go(from + 1);
      });

      this.bar?.addEventListener('click', (event) => {
        const segment = event.target.closest('.stav-slider__seg');
        if (!segment) return;
        stop(event);
        this.resolveHover();
        this.go(this.segments.indexOf(segment));
      });

      window.addEventListener('resize', () => this.go(this._current || 0, true));

      /* Farbkreise unter der Karte: nur Bilder der gewählten Farbe zeigen */
      if (this.hasAttribute('data-stav-multi')) {
        this.closest('product-card')?.addEventListener('change', (event) => {
          const input = event.target;
          if (!input.matches || !input.matches('.product-card__info [type="radio"]')) return;
          let mediaId = '';
          try { mediaId = String(JSON.parse(input.getAttribute('data-variant-media') || '{}').id || ''); } catch (e) {}
          /* Das Theme tauscht das erste Bild selbst aus – danach unsere Bilder filtern */
          /* Farbname steht im Label des Farbkreises (value ist nur eine ID) */
          const label = input.id ? input.closest('product-card').querySelector('label[for="' + CSS.escape(input.id) + '"]') : null;
          const color = (label && label.textContent.trim()) || input.value;
          requestAnimationFrame(() => this.setColor(color, mediaId));
        });
      }

      this.rebuild();
      if (STAV_IS_HOME) this.setupHoverSwap();
    }

    get visibleSlides() {
      return Array.from(this.track.querySelectorAll('.stav-slider__slide')).filter((s) => !s.hidden);
    }

    setColor(color, mediaId) {
      this.track.querySelectorAll('.stav-slider__slide').forEach((slide) => {
        if (slide.hasAttribute('data-stav-first')) return;
        const c = slide.getAttribute('data-stav-color') || 'all';
        let show = c === 'all' || c === color;
        if (show && mediaId && slide.getAttribute('data-stav-media-id') === mediaId) show = false;
        slide.hidden = !show;
      });
      if (this._hoverImg) { this._hoverImg.remove(); this._hoverImg = null; }
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.rebuild();
    }

    /* Strich-Segmente passend zur Anzahl sichtbarer Bilder neu aufbauen */
    rebuild() {
      this.count = this.visibleSlides.length;
      this.toggleAttribute('data-single', this.count < 2);
      if (this.bar) {
        this.bar.innerHTML = '';
        for (let i = 0; i < this.count; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'stav-slider__seg';
          b.setAttribute('aria-label', 'Bild ' + (i + 1) + ' von ' + this.count);
          this.bar.appendChild(b);
        }
      }
      this.segments = Array.from(this.querySelectorAll('.stav-slider__seg'));
      this.update();
    }

    /* Startseite: Maus drauf -> 2. Bild einblenden, Maus weg -> wieder 1. Bild */
    setupHoverSwap() {
      this.addEventListener('mouseenter', () => {
        const secondImg = this.visibleSlides[1]?.querySelector('img');
        if (!secondImg || !STAV_CAN_HOVER.matches || this.index !== 0) return;
        secondImg.loading = 'eager';

        if (!this._hoverImg) {
          const clone = secondImg.cloneNode(true);
          clone.removeAttribute('loading');
          clone.removeAttribute('fetchpriority');
          clone.classList.remove('product-card__image--primary');
          clone.classList.add('stav-slider__hover-img');
          clone.setAttribute('aria-hidden', 'true');
          this.insertBefore(clone, this.track.nextSibling);
          this._hoverImg = clone;
        }

        this._hoverImg.style.height = this.track.clientHeight + 'px';
        requestAnimationFrame(() => this._hoverImg?.classList.add('is-visible'));
        this._hovering = true;
        this.markActive(1);
      });

      this.addEventListener('mouseleave', () => {
        if (!this._hovering) return;
        this._hovering = false;
        this._hoverImg?.classList.remove('is-visible');
        this.markActive(this.index);
      });
    }

    /* Wird die Einblendung gerade gezeigt, springt der Slider unsichtbar auf Bild 2,
       damit Pfeile/Strich von dort aus weiterblättern. */
    resolveHover() {
      if (!this._hovering) return this.index;
      this._hovering = false;
      this.track.scrollTo({ left: this.track.clientWidth, behavior: 'auto' });
      if (this._hoverImg) {
        this._hoverImg.style.transition = 'none';
        this._hoverImg.classList.remove('is-visible');
        requestAnimationFrame(() => { if (this._hoverImg) this._hoverImg.style.transition = ''; });
      }
      return 1;
    }

    get index() {
      const width = this.track.clientWidth || 1;
      return Math.max(0, Math.min(this.count - 1, Math.round(Math.abs(this.track.scrollLeft) / width)));
    }

    go(i, instant = false) {
      const target = Math.max(0, Math.min(this.count - 1, i));
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: instant ? 'auto' : 'smooth' });
    }

    markActive(i) {
      (this.segments || []).forEach((segment, n) => segment.classList.toggle('is-active', n === i));
      this.toggleAttribute('data-first', i === 0);
      this.toggleAttribute('data-last', i === this.count - 1);
    }

    update() {
      const i = this.index;
      this._current = i;
      if (!this._hovering) this.markActive(i);
    }
  }

  customElements.define('stav-card-slider', StavCardSlider);
}
